import React from "react";
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { openDirections } from "../../../utils/mapNavigation";

/**
 * @param {{ status: string, place?: object }} matchState — from useHousePickGooglePlaceMatches
 */
export default function HousePickGooglePlacesRow({ matchState, compact = false }) {
  if (!matchState || matchState.status === "idle") return null;

  if (matchState.status === "loading") {
    return (
      <View style={[styles.row, compact && styles.rowCompact]} accessibilityLabel="Loading Google Places match">
        <ActivityIndicator size="small" color="#9C8A80" style={styles.loader} />
        <Text style={styles.loadingText}>Matching Google Places…</Text>
      </View>
    );
  }

  if (matchState.status === "none") {
    return (
      <View style={[styles.row, styles.rowMuted, compact && styles.rowCompact]}>
        <Ionicons name="search-outline" size={compact ? 14 : 15} color="#9C8A80" style={styles.icon} />
        <Text style={styles.unmatchedText}>No Google Places match found for this result.</Text>
      </View>
    );
  }

  if (matchState.status === "error") {
    return (
      <View style={[styles.row, styles.rowMuted, compact && styles.rowCompact]}>
        <Ionicons name="alert-circle-outline" size={compact ? 14 : 15} color="#9C8A80" style={styles.icon} />
        <Text style={styles.unmatchedText}>Google Places match unavailable right now.</Text>
      </View>
    );
  }

  if (matchState.status !== "matched" || !matchState.place) return null;

  const p = matchState.place;
  const ratingLabel =
    typeof p.rating === "number" && p.rating > 0
      ? `${p.rating.toFixed(1)}★${p.userRatingCount ? ` (${p.userRatingCount})` : ""}`
      : null;

  return (
    <TouchableOpacity
      style={[styles.row, compact && styles.rowCompact]}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={`Open ${p.name} in maps from Google Places`}
      onPress={async () => {
        const lat = p.latitude;
        const lng = p.longitude;
        const hasCoords =
          typeof lat === "number" &&
          typeof lng === "number" &&
          Number.isFinite(lat) &&
          Number.isFinite(lng) &&
          !(lat === 0 && lng === 0);
        await openDirections(
          hasCoords
            ? { latitude: lat, longitude: lng, label: p.name, address: p.address || undefined }
            : { address: p.address || p.name, label: p.name }
        );
      }}
    >
      <Ionicons name="logo-google" size={compact ? 14 : 15} color="#7D695F" style={styles.icon} />
      <View style={styles.textCol}>
        <Text style={styles.label} numberOfLines={1}>
          Google Places
        </Text>
        <Text style={styles.placeName} numberOfLines={2}>
          {p.name}
          {ratingLabel ? <Text style={styles.rating}> · {ratingLabel}</Text> : null}
        </Text>
        {!!p.address && (
          <Text style={styles.placeAddress} numberOfLines={2}>
            {p.address}
          </Text>
        )}
      </View>
      <Ionicons name="chevron-forward" size={18} color="#C4B5AD" />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  row: {
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: "#F8F1ED",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5D9D2",
  },
  rowCompact: {
    marginTop: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  rowMuted: {
    backgroundColor: "#FAF7F5",
    borderStyle: "dashed",
  },
  loader: {
    marginRight: 2,
  },
  loadingText: {
    flex: 1,
    fontSize: 13,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_400Regular",
  },
  unmatchedText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 17,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_400Regular",
  },
  icon: {
    marginTop: 2,
    alignSelf: "flex-start",
  },
  textCol: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 11,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_700Bold",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  placeName: {
    fontSize: 14,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
    lineHeight: 18,
  },
  rating: {
    fontSize: 13,
    color: "#7D695F",
    fontFamily: "AbhayaLibre_400Regular",
  },
  placeAddress: {
    marginTop: 2,
    fontSize: 12,
    color: "#7D695F",
    fontFamily: "AbhayaLibre_400Regular",
    lineHeight: 16,
  },
});
